import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import type { Booking } from 'repositories/BerthApi/types/booking'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'

import { BOOKING_STATUS } from 'constants/Booking'

import type { Berth } from '../types'
import { ADDITIONAL_COLUMN_INDEXES } from '../utils/bookingParameters'
import { getApiErrorMessage } from '../utils/berthManagementHelpers'
import {
  EMPTY_FORM_VALUES,
  toFormValues,
  toUpdateBookingParams,
  toUpdateRepeatBookingParams,
  toUpdateSpecificInfoParams,
  toUpdateUnassignedBookingParams,
} from '../utils/editBookingMappers'
import { splitEntryInstructionTime } from '../utils/editBookingNormalize'
import {
  EditBookingFormValues,
  EditBookingResolverContext,
  FIXED_CARGO_COLUMN_KEYS,
  editBookingResolver,
} from '../utils/editBookingValidation'

import { useMasterIntegration } from './useMasterIntegration'
import { useUpdateBooking } from './useUpdateBooking'
import { useUpdateBookingStatus } from './useUpdateBookingStatus'
import { useUpdateReceptBooking } from './useUpdateReceptBooking'
import { useUpdateRepeatBooking } from './useUpdateRepeatBooking'
import { useUpdateSpecificInfo } from './useUpdateSpecificInfo'
import { useUpdateUnassignedBooking } from './useUpdateUnassignedBooking'

const CARGO_FIELDS = [
  'importFlag',
  'exportFlag',
  ...FIXED_CARGO_COLUMN_KEYS,
  ...ADDITIONAL_COLUMN_INDEXES.flatMap(
    (i) => [`importAdditionalColumn${i}`, `exportAdditionalColumn${i}`] as const
  ),
] as const

const BASIC_FIELDS = [
  'berthId',
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'companyName',
  'vehicleControllerName',
  'vehicleControllerPhone',
  'vehicleControllerEmail',
  'driverCompanyName',
  'vehicleNumber',
  'vehicleCategory',
  'driverName',
  'driverPhone',
  'driverEmail',
  'companyId',
  'driverCompanyId',
  'driverId',
  'vehicleControllerId',
  ...CARGO_FIELDS,
] as const satisfies readonly (keyof EditBookingFormValues)[]

const ADMIN_NOTE_FIELDS = ['adminNote'] as const satisfies readonly (keyof EditBookingFormValues)[]

const ENTRY_INSTRUCTION_FIELDS = [
  'driverDateTime',
  'driverCombinedTime',
  'skipInstructionTime',
] as const satisfies readonly (keyof EditBookingFormValues)[]

type UseEditBookingArgs = {
  booking: Booking | null
  tenantId: string | null
  isUnassigned: boolean
  berths: Berth[]
  onNotify: (payload: SnackbarPayload) => void
  // 保存成功後にフォーム全体が dirty 0 になったら呼ばれる（モーダルクローズ用）。
  onClose: () => void
}

type UseEditBookingResult = {
  form: UseFormReturn<EditBookingFormValues, EditBookingResolverContext>
  apiError: string | null
  isSubmittingBasic: boolean
  isSubmittingAdminNote: boolean
  isSubmittingEntryInstruction: boolean
  isBasicDirty: boolean
  isAdminNoteDirty: boolean
  isEntryInstructionDirty: boolean
  isFormDirty: boolean
  isFinish: boolean
  handleSubmitBasic: () => void
  handleSubmitAdminNote: () => void
  handleSubmitEntryInstruction: () => void
  masterIntegration: ReturnType<typeof useMasterIntegration>
}

export const useEditBooking = ({
  booking,
  tenantId,
  isUnassigned,
  berths,
  onNotify,
  onClose,
}: UseEditBookingArgs): UseEditBookingResult => {
  const initialValues = useMemo<EditBookingFormValues>(
    () => (booking ? toFormValues(booking) : EMPTY_FORM_VALUES),
    [booking]
  )

  const form = useForm<EditBookingFormValues, EditBookingResolverContext>({
    defaultValues: initialValues,
    resolver: editBookingResolver,
    context: { isUnassigned },
    mode: 'onSubmit',
  })

  const [apiError, setApiError] = useState<string | null>(null)

  // dirty は render 中にアクセスしないと react-hook-form の proxy が購読せず effect 内で
  // 常に false になるため、ここで読みつつ ref に退避する（effect の依存には含めない）。
  const { isDirty: isFormDirtyRhf } = form.formState
  const isFormDirtyRef = useRef(isFormDirtyRhf)
  isFormDirtyRef.current = isFormDirtyRhf

  // booking データが実際に変化したときだけフォームを同期する。
  // dirty フラグの変化（保存成功で true→false になる等）を依存に含めると、
  // 保存直後に refetch がまだ旧データを返す瞬間に「旧 initialValues で reset」が走り、
  // 最新値に切り替わる前に一瞬古い情報が見えるちらつきになる。booking 参照を起点にする。
  const syncedBookingRef = useRef(booking)
  useEffect(() => {
    if (syncedBookingRef.current === booking) return
    const isSameBooking = booking?.bookingId === syncedBookingRef.current?.bookingId
    syncedBookingRef.current = booking
    // 同一予約の背景 refetch 中にユーザーが編集していたら、入力を上書きしない。
    if (isSameBooking && isFormDirtyRef.current) return
    form.reset(initialValues)
    setApiError(null)
  }, [booking, initialValues, form])

  const masterIntegration = useMasterIntegration({ form })

  const updateBookingMutation = useUpdateBooking()
  const updateUnassignedBookingMutation = useUpdateUnassignedBooking()
  const updateReceptBookingMutation = useUpdateReceptBooking()
  const updateRepeatBookingMutation = useUpdateRepeatBooking()
  const updateBookingStatusMutation = useUpdateBookingStatus()
  const updateSpecificInfoMutation = useUpdateSpecificInfo()

  const isSubmittingBasic =
    updateBookingMutation.isPending ||
    updateUnassignedBookingMutation.isPending ||
    updateReceptBookingMutation.isPending ||
    updateRepeatBookingMutation.isPending ||
    updateBookingStatusMutation.isPending
  const isSubmittingAdminNote = updateSpecificInfoMutation.isPending
  const isSubmittingEntryInstruction = updateSpecificInfoMutation.isPending

  const watchedAdminNote = form.watch('adminNote')
  const watchedDriverDateTime = form.watch('driverDateTime')
  const watchedDriverCombinedTime = form.watch('driverCombinedTime')
  const watchedSkipInstructionTime = form.watch('skipInstructionTime')

  const { dirtyFields } = form.formState

  // RHF の dirtyFields を一次条件にすることで、保存成功時の resetField で
  // 即座にクリアされる。trim 比較を AND で挟むのは「空白だけの編集を dirty 扱いしない」
  // セマンティクスを維持するため。
  const isAdminNoteDirty =
    !!dirtyFields.adminNote && watchedAdminNote.trim() !== (booking?.adminNote ?? '').trim()

  const { date: bookingDriverDate, time: bookingDriverTime } = splitEntryInstructionTime(
    booking?.entryInstructionTime
  )
  const isEntryInstructionDirty =
    watchedDriverDateTime !== bookingDriverDate ||
    watchedDriverCombinedTime !== bookingDriverTime ||
    watchedSkipInstructionTime !== (booking?.skipInstructionTime ?? false)

  // 添付資料は予約情報セクションに統合されており、その dirty 判定も isBasicDirty に含める。
  // attachments は配列なので react-hook-form の dirtyFields だけでは差分を拾い切れないため、
  // 実値ベースで「未保存ファイルの有無」「削除指定の有無」「既存添付件数の増減」で判定する。
  const watchedAttachments = form.watch('attachments')
  const watchedDeleteIds = form.watch('deleteAttachmentIds')
  const initialAttachmentIds = useMemo(
    () => (booking?.attachments ?? []).filter((a) => a.deletedAt == null).map((a) => a.id),
    [booking?.attachments]
  )
  const isAttachmentDirty =
    watchedDeleteIds.length > 0 ||
    watchedAttachments.some((a) => a.id == null) ||
    watchedAttachments.filter((a) => a.id != null).length !== initialAttachmentIds.length

  const isBasicDirty = BASIC_FIELDS.some((f) => dirtyFields[f]) || isAttachmentDirty

  const isFormDirty = isBasicDirty || isAdminNoteDirty || isEntryInstructionDirty

  // セクション 2（管理者備考＋入場指示時刻）は PC 版互換で完了済でも編集可能とするため、
  // ここでは isFinish を考慮しない。基本情報セクションのみ isFinish を継承する。
  const isFinish = booking?.status === BOOKING_STATUS.Finish

  // 保存成功時に「他セクションが dirty なら閉じない」判定で使う ref。
  // admin/entry の isDirty は booking 値比較で refetch 待ちのため、effect 監視ではなく onSuccess 内で直接読む。
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])
  const isBasicDirtyRef = useRef(isBasicDirty)
  isBasicDirtyRef.current = isBasicDirty
  const isAdminNoteDirtyRef = useRef(isAdminNoteDirty)
  isAdminNoteDirtyRef.current = isAdminNoteDirty
  const isEntryInstructionDirtyRef = useRef(isEntryInstructionDirty)
  isEntryInstructionDirtyRef.current = isEntryInstructionDirty

  const onSubmitBasic = useCallback(
    (values: EditBookingFormValues) => {
      if (!booking || !booking.bookingId || !tenantId || !booking.warehouseId) return
      const { warehouseId } = booking

      const onError = (error: unknown) => {
        const message = getApiErrorMessage(error)
        setApiError(message)
        onNotify({ message, variant: 'error' })
      }
      const isAssigningFromUnassigned = isUnassigned && !!values.berthId
      const successMessage = isAssigningFromUnassigned
        ? `${berths.find((b) => b.id === values.berthId)?.name ?? ''}の${values.startTime} - ${values.endTime}に予約を割り当てました`
        : '予約情報を更新しました'
      const finalizeSuccess = () => {
        onNotify({ message: successMessage, variant: 'success' })
        // form.reset(all) で一括クリアすると admin/entry の dirty も false になり、
        // booking refetch sync で他セクションの未保存編集が上書きされるため個別 resetField で対応。
        const current = form.getValues()
        for (const field of BASIC_FIELDS) {
          form.resetField(field, { defaultValue: current[field] })
        }
        // 添付資料: 新規追加分（id なし）を defaultValue から除外して再送信防止。
        form.resetField('attachments', {
          defaultValue: current.attachments.filter((a) => a.id != null),
        })
        form.resetField('deleteAttachmentIds', { defaultValue: [] })
        if (!isAdminNoteDirtyRef.current && !isEntryInstructionDirtyRef.current) {
          onCloseRef.current()
        }
      }
      // PC 版 sagas.js:512-519 に揃え、元ステータスが Recept(受付済・未割当用) のときは
      // /berths/booking 系の保存後に /update_status で Arrival(受付済・割当済用) へ昇格させる。
      // この補正を行わないと BE 側で status が Confirmed(予約済) に降格する。
      // /unassign / /update_reception の経路では昇格しない（PC 版 sagas.js:416-453 と整合）。
      const upgradeToArrivalIfNeeded = () => {
        if (booking.status === BOOKING_STATUS.Recept && booking.bookingId) {
          updateBookingStatusMutation.mutate(
            {
              bookingId: booking.bookingId,
              status: BOOKING_STATUS.Arrival,
              tenantId,
            },
            { onSuccess: finalizeSuccess, onError }
          )
          return
        }
        finalizeSuccess()
      }

      if (isUnassigned) {
        if (!values.berthId) {
          // PC 版 (sagas.js:422-426) と同じ分岐: status === Unassigned のときだけ /unassign、
          // それ以外（実質 Recept）は /update_reception。/unassign は受付済を受け付けず BE が拒否するため。
          const params = toUpdateUnassignedBookingParams(values, booking, tenantId, warehouseId)
          if (booking.status === BOOKING_STATUS.Unassigned) {
            updateUnassignedBookingMutation.mutate(params, {
              onSuccess: finalizeSuccess,
              onError,
            })
          } else {
            updateReceptBookingMutation.mutate(params, {
              onSuccess: finalizeSuccess,
              onError,
            })
          }
          return
        }
        updateBookingMutation.mutate(
          toUpdateBookingParams(values, booking, tenantId, warehouseId),
          { onSuccess: upgradeToArrivalIfNeeded, onError }
        )
        return
      }
      if (booking.repeatId) {
        if (!booking.startDate || !booking.startTime || !booking.endDate || !booking.endTime) {
          const message = '繰り返し予約の元日時が取得できないため、保存できません。'
          setApiError(message)
          onNotify({ message, variant: 'error' })
          return
        }
        updateRepeatBookingMutation.mutate(
          toUpdateRepeatBookingParams(values, booking, tenantId, warehouseId),
          { onSuccess: upgradeToArrivalIfNeeded, onError }
        )
        return
      }
      updateBookingMutation.mutate(toUpdateBookingParams(values, booking, tenantId, warehouseId), {
        onSuccess: upgradeToArrivalIfNeeded,
        onError,
      })
    },
    [
      booking,
      tenantId,
      isUnassigned,
      berths,
      updateBookingMutation,
      updateUnassignedBookingMutation,
      updateReceptBookingMutation,
      updateRepeatBookingMutation,
      updateBookingStatusMutation,
      onNotify,
      form,
    ]
  )

  const onSubmitAdminNote = useCallback(
    (values: EditBookingFormValues) => {
      if (!booking || !booking.bookingId) return

      const { date: bookingDate, time: bookingTime } = splitEntryInstructionTime(
        booking.entryInstructionTime
      )
      const params = toUpdateSpecificInfoParams(
        {
          ...values,
          driverDateTime: bookingDate,
          driverCombinedTime: bookingTime,
          skipInstructionTime: booking.skipInstructionTime ?? false,
        },
        booking
      )

      updateSpecificInfoMutation.mutate(params, {
        onSuccess: () => {
          onNotify({ message: '管理者備考を更新しました', variant: 'success' })
          form.resetField('adminNote', { defaultValue: values.adminNote.trim() })
          if (!isBasicDirtyRef.current && !isEntryInstructionDirtyRef.current) {
            onCloseRef.current()
          }
        },
        onError: (error: unknown) => {
          const message = getApiErrorMessage(error)
          setApiError(message)
          onNotify({ message, variant: 'error' })
        },
      })
    },
    [booking, updateSpecificInfoMutation, onNotify, form]
  )

  const onSubmitEntryInstruction = useCallback(
    (values: EditBookingFormValues) => {
      if (!booking || !booking.bookingId) return

      const params = toUpdateSpecificInfoParams(
        { ...values, adminNote: booking.adminNote ?? '' },
        booking
      )

      updateSpecificInfoMutation.mutate(params, {
        onSuccess: () => {
          onNotify({ message: '入場指示を更新しました', variant: 'success' })
          form.resetField('driverDateTime', { defaultValue: values.driverDateTime })
          form.resetField('driverCombinedTime', { defaultValue: values.driverCombinedTime })
          form.resetField('skipInstructionTime', { defaultValue: values.skipInstructionTime })
          if (!isBasicDirtyRef.current && !isAdminNoteDirtyRef.current) {
            onCloseRef.current()
          }
        },
        onError: (error: unknown) => {
          const message = getApiErrorMessage(error)
          setApiError(message)
          onNotify({ message, variant: 'error' })
        },
      })
    },
    [booking, updateSpecificInfoMutation, onNotify, form]
  )

  // Note: バリデーション失敗時、最初にエラーになっているフィールドへ自動スクロール+フォーカスする。
  // スマートフォン表示で画面外の項目にエラーが出ていると、ユーザーが「なぜ保存できないか」気付けないため。
  const scrollToFirstError = useCallback(
    (fields: readonly (keyof EditBookingFormValues)[]) => {
      const errors = form.formState.errors
      const target = fields.find((f) => errors[f])
      if (!target) return
      const el = document.getElementById(target)
      if (!el) return
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      // フォーカス可能要素なら focus も当てる（input/select/textarea）
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLSelectElement ||
        el instanceof HTMLTextAreaElement
      ) {
        el.focus({ preventScroll: true })
      }
    },
    [form]
  )

  // basic 用と admin-note 用でそれぞれ対象フィールドだけを部分的に検証する。
  // RHF の handleSubmit はスキーマ全体を走らせてしまうため、trigger(fieldNames) で部分検証する。
  const handleSubmitBasic = useCallback(() => {
    setApiError(null)
    void form.trigger(BASIC_FIELDS).then((isValid) => {
      if (!isValid) {
        scrollToFirstError(BASIC_FIELDS)
        return
      }
      const values = form.getValues()
      onSubmitBasic(values)
    })
  }, [form, onSubmitBasic, scrollToFirstError])

  const handleSubmitAdminNote = useCallback(() => {
    setApiError(null)
    void form.trigger(ADMIN_NOTE_FIELDS).then((isValid) => {
      if (!isValid) {
        scrollToFirstError(ADMIN_NOTE_FIELDS)
        return
      }
      const values = form.getValues()
      onSubmitAdminNote(values)
    })
  }, [form, onSubmitAdminNote, scrollToFirstError])

  const handleSubmitEntryInstruction = useCallback(() => {
    setApiError(null)
    void form.trigger(ENTRY_INSTRUCTION_FIELDS).then((isValid) => {
      if (!isValid) {
        scrollToFirstError(ENTRY_INSTRUCTION_FIELDS)
        return
      }
      const values = form.getValues()
      onSubmitEntryInstruction(values)
    })
  }, [form, onSubmitEntryInstruction, scrollToFirstError])

  return {
    form,
    apiError,
    isSubmittingBasic,
    isSubmittingAdminNote,
    isSubmittingEntryInstruction,
    isBasicDirty,
    isAdminNoteDirty,
    isEntryInstructionDirty,
    isFormDirty,
    isFinish,
    handleSubmitBasic,
    handleSubmitAdminNote,
    handleSubmitEntryInstruction,
    masterIntegration,
  }
}
